import React from 'react';
import { Loader2, Trophy } from 'lucide-react';

const SPEED_STEPS = [0.8, 1, 1.5, 2];

/**
 * Options modal for the course player.
 * - On mobile, exposes session-level actions (theme, language, pause, complete)
 *   because the desktop floating pills are hidden there.
 * - Audio block (volume / speed / auto-next / auto-scroll) only rendered when audio is available.
 */
const CourseSettingsModal = ({
    open,
    onClose,
    // Audio
    hasAudio = false,
    volume,
    onVolumeChange,
    playbackRate,
    onPlaybackRateChange,
    autoNext,
    onToggleAutoNext,
    autoScroll,
    onToggleAutoScroll,
    // Mobile-only session controls
    theme,
    onToggleTheme,
    language = 'fr',
    onToggleLanguage,
    coursePaused = false,
    onTogglePause,
    onComplete,
    completing = false,
}) => {
    if (!open) return null;
    const fr = language === 'fr';

    const closeThen = (fn) => () => {
        onClose();
        if (fn) fn();
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[120] p-4"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-gray-900 rounded-3xl w-full max-w-sm shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-h-[90vh] overflow-y-auto custom-scrollbar"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close */}
                <div className="flex items-center justify-end mb-2 -mt-1">
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-full text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* ───────── Mobile-only session actions ───────── */}
                <div className="md:hidden space-y-3 mb-6">
                    {/* Language & theme — compact badge style, consistent with account menu */}
                    <div className="flex items-center justify-center gap-2">
                        {onToggleLanguage && (
                            <button
                                onClick={onToggleLanguage}
                                className="px-3 py-2 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600 transition"
                                title={language === 'fr' ? 'Switch to English' : 'Passer en français'}
                            >
                                {language === 'fr' ? 'EN' : 'FR'}
                            </button>
                        )}
                        {onToggleTheme && (
                            <button
                                onClick={onToggleTheme}
                                className="p-2 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600 transition"
                                title={theme === 'dark' ? (fr ? 'Mode clair' : 'Light mode') : (fr ? 'Mode sombre' : 'Dark mode')}
                            >
                                {theme === 'dark' ? (
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                ) : (
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                    </svg>
                                )}
                            </button>
                        )}
                    </div>

                    {/* Pause / Resume session */}
                    {onTogglePause && (
                        <button
                            onClick={closeThen(onTogglePause)}
                            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600 transition text-sm font-semibold"
                        >
                            {coursePaused ? (
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                            ) : (
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            )}
                            {coursePaused
                                ? (fr ? 'Reprendre la session' : 'Resume session')
                                : (fr ? 'Mettre en pause' : 'Pause session')}
                        </button>
                    )}

                    {/* Complete session */}
                    {onComplete && (
                        <button
                            onClick={closeThen(onComplete)}
                            disabled={completing}
                            className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-sm font-bold transition shadow-md shadow-green-600/30 disabled:opacity-50"
                        >
                            {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
                            {fr ? 'Terminer la session' : 'Complete session'}
                        </button>
                    )}
                </div>

                {hasAudio && (
                    <>
                        {/* Title (audio block) */}
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-6">
                            {fr ? 'Option audio' : 'Audio options'}
                        </h2>

                        {/* Volume */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                {fr ? 'Volume globale' : 'Volume'}
                            </label>
                            <input
                                type="range"
                                min={0}
                                max={10}
                                value={Math.round(volume * 10)}
                                onChange={(e) => onVolumeChange(Number(e.target.value) / 10)}
                                className="w-full accent-blue-500 cursor-pointer"
                            />
                        </div>

                        {/* Playback rate */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                {fr ? 'Vitesse de lecture' : 'Playback speed'}
                            </label>
                            <div className="grid grid-cols-4 gap-2">
                                {SPEED_STEPS.map((s) => {
                                    const active = playbackRate === s;
                                    return (
                                        <button
                                            key={s}
                                            onClick={() => onPlaybackRateChange(s)}
                                            className={`py-2 rounded-full text-sm font-bold transition border-2 ${
                                                active
                                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400'
                                                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                                            }`}
                                        >
                                            x{s}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Auto next chapter */}
                        <div className="mb-4 flex items-center justify-between gap-4">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex-1">
                                {fr ? 'Changement de section automatique' : 'Auto-advance section'}
                            </span>
                            <button
                                onClick={onToggleAutoNext}
                                role="switch"
                                aria-checked={autoNext}
                                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                                    autoNext ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                                }`}
                            >
                                <span
                                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                                        autoNext ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                                />
                            </button>
                        </div>

                        {/* Auto scroll */}
                        <div className="mb-6 flex items-center justify-between gap-4">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex-1">
                                {fr ? 'Défilement de la page automatique' : 'Auto-scroll page'}
                            </span>
                            <button
                                onClick={onToggleAutoScroll}
                                role="switch"
                                aria-checked={autoScroll}
                                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                                    autoScroll ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                                }`}
                            >
                                <span
                                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                                        autoScroll ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                                />
                            </button>
                        </div>
                    </>
                )}

                {/* Close action */}
                <button
                    onClick={onClose}
                    className="w-full py-3 rounded-full font-bold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-600/30"
                >
                    {fr ? 'Fermer' : 'Close'}
                </button>
            </div>
        </div>
    );
};

export default CourseSettingsModal;
